// シェイプの幾何 → Path2D コマンド (純ロジック寄り: Path2D の生成は呼び出し側)。
// 変換行列は ctx.setTransform で適用するので、ここでは幾何座標のまま扱う。

import type { ShapeGeometry } from '@/types/document'
import type { Pt, Rect, Mat3 } from '@/types/geometry'
import { mat3Apply } from '@/types/geometry'

export type PathCmd = ['M', number, number] | ['L', number, number] | ['C', number, number, number, number, number, number] | ['Z']

export function geometryCommands(g: ShapeGeometry): PathCmd[] {
  const out: PathCmd[] = []
  switch (g.type) {
    case 'rect': {
      const r = Math.min(g.radius, g.w / 2, g.h / 2)
      if (r <= 0) {
        out.push(['M', g.x, g.y], ['L', g.x + g.w, g.y], ['L', g.x + g.w, g.y + g.h], ['L', g.x, g.y + g.h], ['Z'])
      } else {
        const k = 0.5522847498 * r
        const x0 = g.x, y0 = g.y, x1 = g.x + g.w, y1 = g.y + g.h
        out.push(['M', x0 + r, y0], ['L', x1 - r, y0], ['C', x1 - r + k, y0, x1, y0 + r - k, x1, y0 + r], ['L', x1, y1 - r], ['C', x1, y1 - r + k, x1 - r + k, y1, x1 - r, y1], ['L', x0 + r, y1], ['C', x0 + r - k, y1, x0, y1 - r + k, x0, y1 - r], ['L', x0, y0 + r], ['C', x0, y0 + r - k, x0 + r - k, y0, x0 + r, y0], ['Z'])
      }
      break
    }
    case 'ellipse': {
      const cx = g.x + g.w / 2, cy = g.y + g.h / 2, rx = g.w / 2, ry = g.h / 2
      const k = 0.5522847498
      out.push(['M', cx + rx, cy], ['C', cx + rx, cy + ry * k, cx + rx * k, cy + ry, cx, cy + ry], ['C', cx - rx * k, cy + ry, cx - rx, cy + ry * k, cx - rx, cy], ['C', cx - rx, cy - ry * k, cx - rx * k, cy - ry, cx, cy - ry], ['C', cx + rx * k, cy - ry, cx + rx, cy - ry * k, cx + rx, cy], ['Z'])
      break
    }
    case 'polygon': {
      const cx = g.x + g.w / 2, cy = g.y + g.h / 2, rx = g.w / 2, ry = g.h / 2
      const n = Math.max(3, Math.round(g.sides))
      const star = g.star > 0
      const count = star ? n * 2 : n
      for (let i = 0; i < count; i++) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / count
        const k = star && i % 2 === 1 ? 1 - g.star : 1
        const x = cx + Math.cos(a) * rx * k, y = cy + Math.sin(a) * ry * k
        out.push(i === 0 ? ['M', x, y] : ['L', x, y])
      }
      out.push(['Z'])
      break
    }
    case 'line':
      out.push(['M', g.x1, g.y1], ['L', g.x2, g.y2])
      break
    case 'path': {
      g.points.forEach((p, i) => {
        if (i === 0) out.push(['M', p.x, p.y])
        else {
          const prev = g.points[i - 1]!
          const c1 = prev.out ?? { x: prev.x, y: prev.y }
          const c2 = p.in ?? { x: p.x, y: p.y }
          if (!prev.out && !p.in) out.push(['L', p.x, p.y])
          else out.push(['C', c1.x, c1.y, c2.x, c2.y, p.x, p.y])
        }
      })
      if (g.closed && g.points.length > 1) {
        const last = g.points[g.points.length - 1]!, first = g.points[0]!
        if (last.out || first.in) out.push(['C', (last.out ?? last).x, (last.out ?? last).y, (first.in ?? first).x, (first.in ?? first).y, first.x, first.y])
        out.push(['Z'])
      }
      break
    }
  }
  return out
}

/** 幾何の境界 (変換前) */
export function geometryBounds(g: ShapeGeometry): Rect {
  switch (g.type) {
    case 'rect':
    case 'ellipse':
    case 'polygon':
      return { x: g.x, y: g.y, w: g.w, h: g.h }
    case 'line':
      return { x: Math.min(g.x1, g.x2), y: Math.min(g.y1, g.y2), w: Math.abs(g.x2 - g.x1), h: Math.abs(g.y2 - g.y1) }
    case 'path': {
      const pts: Pt[] = []
      for (const p of g.points) {
        pts.push(p)
        if (p.in) pts.push(p.in)
        if (p.out) pts.push(p.out)
      }
      if (!pts.length) return { x: 0, y: 0, w: 0, h: 0 }
      const x0 = Math.min(...pts.map(p => p.x)), y0 = Math.min(...pts.map(p => p.y))
      return { x: x0, y: y0, w: Math.max(...pts.map(p => p.x)) - x0, h: Math.max(...pts.map(p => p.y)) - y0 }
    }
  }
}

/** 変換後の境界 (線幅の余白込み、整数) */
export function shapeBounds(g: ShapeGeometry, m: Mat3, strokePad: number): Rect {
  const b = geometryBounds(g)
  const pts = [
    { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y }, { x: b.x, y: b.y + b.h }, { x: b.x + b.w, y: b.y + b.h }
  ].map(p => ({ x: m[0] * p.x + m[3] * p.y + m[6], y: m[1] * p.x + m[4] * p.y + m[7] }))
  const scale = Math.max(Math.hypot(m[0], m[1]), Math.hypot(m[3], m[4]))
  const pad = strokePad * scale + 2
  const x0 = Math.floor(Math.min(...pts.map(p => p.x)) - pad), y0 = Math.floor(Math.min(...pts.map(p => p.y)) - pad)
  const x1 = Math.ceil(Math.max(...pts.map(p => p.x)) + pad), y1 = Math.ceil(Math.max(...pts.map(p => p.y)) + pad)
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }
}

/**
 * 幾何を行列で文書座標へ写し、曲線を折れ線に平坦化したサブパスの点列にする (パスの境界線を描く用)。
 * tol は曲線 1 セグメントあたりの分割数の目安 (px)。
 */
export function flattenGeometry(g: ShapeGeometry, m: Mat3, tol = 2): Pt[][] {
  const out: Pt[][] = []
  let cur: Pt[] = []
  let start: Pt | null = null
  const push = (p: Pt) => cur.push(mat3Apply(m, p))
  for (const cmd of geometryCommands(g)) {
    if (cmd[0] === 'M') {
      if (cur.length > 1) out.push(cur)
      cur = []
      start = { x: cmd[1], y: cmd[2] }
      push(start)
    } else if (cmd[0] === 'L') push({ x: cmd[1], y: cmd[2] })
    else if (cmd[0] === 'C') {
      const last = cur[cur.length - 1]!
      const p0 = last, p1 = mat3Apply(m, { x: cmd[1], y: cmd[2] }), p2 = mat3Apply(m, { x: cmd[3], y: cmd[4] }), p3 = mat3Apply(m, { x: cmd[5], y: cmd[6] })
      const approx = Math.hypot(p1.x - p0.x, p1.y - p0.y) + Math.hypot(p2.x - p1.x, p2.y - p1.y) + Math.hypot(p3.x - p2.x, p3.y - p2.y)
      const n = Math.max(2, Math.min(200, Math.ceil(approx / tol)))
      for (let i = 1; i <= n; i++) {
        const t = i / n, u = 1 - t
        cur.push({ x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x, y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y })
      }
    } else if (cmd[0] === 'Z') {
      if (start) push(start)
    }
  }
  if (cur.length > 1) out.push(cur)
  return out
}
